/* eslint-disable max-len */
// =============================================================================
// Xuất file Kê khai thành tích cá nhân — Giải thưởng Nữ sinh KHCN (scene GTNS).
// Làm tương tự QCV (qcv.exportProfile.js) nhưng nội dung theo cấu trúc GTNS:
//   Thông tin cá nhân · A. Học tập · B. KHCN (mục 1-7) · C. Khen thưởng ·
//   D. Hoạt động Đoàn, Hội · E. Ưu tiên.
// Toàn bộ nội dung được dựng dạng HTML rồi nhồi vào template tối giản
// (GTNS_Ho_so.docx có 1 placeholder {~~content}) qua docxtemplater HTML module.
// =============================================================================
import dayjs from "dayjs";
import PizZip from "pizzip";
import PizZipUtils from "pizzip/utils/index.js";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";

import sample from "../assets/docs/GTNS_Ho_so.docx";
import HTMLModule from "./docxtemplater-html-module";

import {
  APP_SCENE,
  ACHIEVEMENT_TYPE,
  CATEGORY,
  FORM_CONSTANTS,
  REWARD_CONSTANTS,
  REPORTS_CONSTANTS,
  RESEARCH_CONSTANTS,
  SOLUTIONS_CONSTANTS,
} from "../constants";
import { KEYS } from "../constants/enums";
import {
  GTNS2026_PRIORITY_FIELDS,
} from "../constants/gtns2026.config";

const loadFile = (url, callback) =>
  PizZipUtils.getBinaryContent(url, callback);

const esc = (v) =>
  (v === undefined || v === null ? "" : String(v))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const findTitle = (options, value) =>
  (options || []).find((o) => o.value === value)?.title || "";

const minhChung = (domain, item) => {
  const url = item?.link || item?.preview || "";
  if (!url) return "";
  const href = /^https?:\/\//i.test(url) ? url : `${domain || ""}${url}`;
  return `<a href="${esc(href)}">Minh chứng</a>`;
};
const minhChungFile = (domain, file) => {
  if (!file) return "";
  const href = /^https?:\/\//i.test(file) ? file : `${domain || ""}${file}`;
  return `<a href="${esc(href)}">Minh chứng</a>`;
};

const fmtDate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "");

// Dựng 1 bảng từ danh sách cột [{ header, get }] và danh sách dòng.
const table = (columns, rows) => {
  if (!rows || rows.length === 0)
    return '<p style="font-style:italic">Không có</p>';
  const thead =
    "<tr>" +
    '<td style="background-color:#e8eef7"><b>STT</b></td>' +
    columns
      .map(
        (c) =>
          `<td style="background-color:#e8eef7"><b>${esc(c.header)}</b></td>`
      )
      .join("") +
    "</tr>";
  const body = rows
    .map(
      (row, i) =>
        "<tr>" +
        `<td>${i + 1}</td>` +
        columns.map((c) => `<td>${c.get(row, i)}</td>`).join("") +
        "</tr>"
    )
    .join("");
  return `<table>${thead}${body}</table>`;
};

const sectionTitle = (text) =>
  `<h2 style="color:#233871">${esc(text)}</h2>`;
const subTitle = (text) => `<p><b>${esc(text)}</b></p>`;
const note = (text) =>
  `<p style="font-style:italic">Lưu ý: ${esc(text)}</p>`;

export default function exportGtnsProfile({
  profile = {},
  values = {},
  achievementData = {},
  reward = [],
  socialActivity = [],
  priority = {},
} = {}) {
  const domain = process.env.REACT_APP_DOMAIN;

  loadFile(sample, (error, content) => {
    if (error) throw error;

    const zip = new PizZip(content);
    const doc = new Docxtemplater(zip, {
      linebreaks: true,
      paragraphLoop: true,
      modules: [new HTMLModule({ ignoreUnknownTags: true })],
    });

    const get = (key) => achievementData[key] || [];

    // -------- Cột cho từng loại thành tích --------
    // B.1 / B.2 — Sáng chế / giải pháp hữu ích (theo mẫu GTNS_FINAL.docx):
    // Tên sáng chế/giải pháp | Phân loại | Số TG chính | Số tác giả | Vai trò | Minh chứng
    const solutionCols = (typeOptions) => [
      { header: "Tên sáng chế/giải pháp", get: (r) => esc(r.title) },
      { header: "Phân loại", get: (r) => esc(findTitle(typeOptions, r.type)) },
      { header: "Số TG chính", get: (r) => esc(r.numberMain) },
      { header: "Số tác giả", get: (r) => esc(r.numberAuthor) },
      {
        header: "Vai trò",
        get: (r) => esc(findTitle(FORM_CONSTANTS.ROLE_OPTIONS, r.role)),
      },
      { header: "Minh chứng", get: (r) => minhChung(domain, r) },
    ];
    const articleCols = (typeOptions, roleOptions) => [
      { header: "Tên công bố", get: (r) => esc(r.title) },
      { header: "Phân loại", get: (r) => esc(findTitle(typeOptions, r.type)) },
      { header: "Số TG chính", get: (r) => esc(r.numberMain) },
      { header: "Số tác giả", get: (r) => esc(r.numberAuthor) },
      { header: "Vai trò", get: (r) => esc(findTitle(roleOptions, r.role)) },
      { header: "Minh chứng", get: (r) => minhChung(domain, r) },
    ];
    const detaiCols = (typeKey) => [
      { header: "Tên đề tài", get: (r) => esc(r.title) },
      {
        header: "Loại giải thưởng",
        get: (r) => esc(findTitle(REWARD_CONSTANTS.TYPE_OPTIONS[typeKey], r.type)),
      },
      {
        header: "Vai trò",
        get: (r) => esc(findTitle(FORM_CONSTANTS.DETAI_ROLE_OPTIONS, r.role)),
      },
      { header: "Ngày quyết định", get: (r) => esc(r.decisionDay || fmtDate(r.issueAt)) },
      { header: "Minh chứng", get: (r) => minhChung(domain, r) },
    ];
    // B.7 — Huy chương / Giải thưởng (theo mẫu GTNS_FINAL.docx):
    // Tên Giải thưởng | Loại giải thưởng | Ngày quyết định | Cơ quan, tổ chức ra quyết định khen thưởng | Minh chứng
    const medalCols = (typeKey) => [
      { header: "Tên Giải thưởng", get: (r) => esc(r.title) },
      {
        header: "Loại giải thưởng",
        get: (r) => esc(findTitle(REWARD_CONSTANTS.TYPE_OPTIONS[typeKey], r.type)),
      },
      { header: "Ngày quyết định", get: (r) => esc(r.decisionDay || fmtDate(r.issueAt)) },
      {
        header: "Cơ quan, tổ chức ra quyết định khen thưởng",
        get: (r) => esc(r.unit),
      },
      { header: "Minh chứng", get: (r) => minhChung(domain, r) },
    ];

    // -------- Thông tin cá nhân --------
    const info = [
      ["Họ và tên", esc(profile.name)],
      ["Ngày sinh", esc(fmtDate(profile.birth))],
      ["Giới tính", esc(findTitle(FORM_CONSTANTS.GENDER_OPTIONS, profile.gender))],
      ["Dân tộc", esc(profile.ethnic)],
      ["Số điện thoại", esc(profile.phone)],
      ["Email", esc(profile.email)],
      ["Website/Facebook", esc(profile.website)],
      ["Đơn vị đề cử", esc(values.nominator)],
      [
        "Đoàn thể chính trị",
        esc(findTitle(FORM_CONSTANTS.POLITICAL_UNION_OPTIONS, values.politicalUnion)),
      ],
      ["Ngoại ngữ", esc(values.languague)],
      ["Tên trường/học viện đang theo học", esc(values.university)],
      ["Tên Khoa/Viện", esc(values.faculty)],
      ["Sinh viên năm thứ", esc(values.studentYear)],
      ["Ngành học", esc(values.major)],
      [
        "Nhóm ngành đăng ký",
        esc(findTitle(FORM_CONSTANTS.FIELD_OPTIONS[APP_SCENE], values.field)),
      ],
    ];
    // Theo mẫu GTNS_FINAL.docx: thông tin cá nhân là các dòng "Nhãn: giá trị".
    const infoHtml = info
      .map(([k, v]) => `<p><b>${esc(k)}:</b> ${v}</p>`)
      .join("");

    // -------- C. Khen thưởng / D. Hoạt động Đoàn Hội (JSON) --------
    const rewardTable = table(
      [
        { header: "Nội dung khen thưởng", get: (r) => esc(r.rewardContent) },
        {
          header: "Cấp khen thưởng",
          get: (r) =>
            esc(findTitle(FORM_CONSTANTS.REWARD_LEVEL_GTNS_OPTIONS, r.rewardType)),
        },
        {
          header: "Minh chứng",
          get: (r) => minhChungFile(domain, r.rewardPreviewFile),
        },
      ],
      reward || []
    );
    const socialTable = table(
      [
        {
          header: "Hình thức và nội dung thành tích",
          get: (r) => esc(r.socialActivityContent),
        },
        {
          header: "Minh chứng",
          get: (r) => minhChungFile(domain, r.socialActivityPreviewFile),
        },
      ],
      socialActivity || []
    );

    // -------- E. Ưu tiên --------
    // Theo mẫu: mỗi mục ưu tiên là 1 dòng có ô ☐ (☒ nếu được chọn).
    const priorityHtml = GTNS2026_PRIORITY_FIELDS.map(
      (f) => `<p>${priority?.[f.key] ? "☒" : "☐"} ${esc(f.label)}</p>`
    ).join("");

    // -------- Ghép toàn bộ nội dung --------
    const bodyHtml = [
      sectionTitle("THÔNG TIN CÁ NHÂN"),
      infoHtml,

      sectionTitle("A. THÀNH TÍCH HỌC TẬP"),
      `<p><b>1. Điểm TB chung học tập (quy đổi theo thang điểm 4):</b> ${esc(
        values.gpa
      )}</p>`,
      `<p><b>2. File Bảng điểm học tập:</b> ${
        minhChungFile(domain, values.uploadGpa) || ""
      }</p>`,

      sectionTitle("B. THÀNH TÍCH KHOA HỌC CÔNG NGHỆ"),
      subTitle(
        "1. Có bằng độc quyền sáng chế, giải pháp hữu ích thuộc các nhóm ngành xét chọn"
      ),
      table(
        solutionCols(SOLUTIONS_CONSTANTS.TYPE_OPTIONS),
        get(ACHIEVEMENT_TYPE.SOLUTION)
      ),
      subTitle(
        "2. Có sáng chế/giải pháp hữu ích đã được chấp nhận đơn hợp lệ thuộc các nhóm ngành xét chọn"
      ),
      table(
        solutionCols(SOLUTIONS_CONSTANTS.TYPE_GTNS_OPTIONS),
        get(ACHIEVEMENT_TYPE.SOLUTION + CATEGORY.GTNS)
      ),
      subTitle(
        "3. Bài báo khoa học đăng trên tạp chí quốc tế/quốc gia thuộc các nhóm ngành xét giải"
      ),
      note("Ứng viên chỉ khai các tạp chí thuộc danh mục của Hội đồng chức danh GSNN"),
      subTitle("3.1. Bài báo khoa học trên tạp chí uy tín quốc tế"),
      table(
        articleCols(
          RESEARCH_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL,
          RESEARCH_CONSTANTS.ROLE_OPTIONS
        ),
        get(ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.INTERNATIONAL)
      ),
      subTitle("3.2. Bài báo khoa học đăng trên tạp chí trong nước"),
      table(
        articleCols(
          RESEARCH_CONSTANTS.TYPE_OPTIONS.DOMESTIC,
          RESEARCH_CONSTANTS.ROLE_OPTIONS
        ),
        get(ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.DOMESTIC)
      ),
      subTitle(
        "4. Bài báo khoa học đăng toàn văn trên kỷ yếu hoặc poster được báo cáo tại hội nghị/hội thảo quốc tế/quốc gia thuộc các nhóm ngành xét giải"
      ),
      note("Không xét với các bài đăng tóm tắt và poster trưng bày"),
      table(
        articleCols(
          REPORTS_CONSTANTS.TYPE_OPTIONS.ALL,
          REPORTS_CONSTANTS.ROLE_OPTIONS
        ),
        get(ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT)
      ),
      subTitle(
        "5. Tham gia đề tài nghiên cứu khoa học sinh viên thuộc các nhóm ngành xét giải tham gia báo cáo, dự thi"
      ),
      note("Minh chứng là Bằng khen/Giấy khen đề tài"),
      subTitle("5.1. Đề tài nghiên cứu khoa học cấp Bộ và tương đương đã nghiệm thu"),
      table(detaiCols(KEYS.NCKHCB), get(ACHIEVEMENT_TYPE.REWARD + KEYS.NCKHCB)),
      subTitle("5.2. Đề tài nghiên cứu khoa học cấp Trường và tương đương đã nghiệm thu"),
      table(detaiCols(KEYS.NCKHCT), get(ACHIEVEMENT_TYPE.REWARD + KEYS.NCKHCT)),
      subTitle(
        "6. Tham gia đề tài, dự án nghiên cứu khoa học được Hội đồng khoa học có thẩm quyền nghiệm thu, xếp loại đạt yêu cầu trở lên"
      ),
      note("Minh chứng là Biên bản nghiệm thu đề tài, Quyết định xếp loại"),
      table(detaiCols(KEYS.NCKHDA), get(ACHIEVEMENT_TYPE.REWARD + KEYS.NCKHDA)),
      subTitle(
        "7. Đạt Huy chương/Giải thưởng các cuộc thi học thuật thuộc các nhóm ngành xét giải"
      ),
      subTitle("7.1. Huy chương/Giải thưởng cấp quốc tế"),
      table(medalCols(KEYS.HTQT), get(ACHIEVEMENT_TYPE.REWARD + KEYS.HTQT)),
      subTitle("7.2. Huy chương/Giải thưởng cấp quốc gia"),
      table(medalCols(KEYS.HTQG), get(ACHIEVEMENT_TYPE.REWARD + KEYS.HTQG)),
      subTitle("7.3. Huy chương/Giải thưởng cấp Bộ, ban, ngành, tỉnh"),
      table(medalCols(KEYS.HTCS), get(ACHIEVEMENT_TYPE.REWARD + KEYS.HTCS)),

      sectionTitle("C. KHEN THƯỞNG THÀNH TÍCH KHCN"),
      rewardTable,

      sectionTitle("D. THÀNH TÍCH HOẠT ĐỘNG ĐOÀN, HỘI"),
      socialTable,

      sectionTitle("E. ƯU TIÊN"),
      priorityHtml,

      '<p>&nbsp;</p>',
      "<p>Tôi xin cam đoan những thông tin trên là đúng sự thật và hoàn toàn chịu trách nhiệm trước pháp luật, Ban tổ chức Giải thưởng.</p>",
      "<p>&nbsp;</p>",
      '<table style="border:none"><tr>' +
        '<td style="border:none;width:50%;text-align:center"><b>Xác nhận của lãnh đạo đơn vị</b><br/>(Ký tên, đóng dấu)</td>' +
        '<td style="border:none;width:50%;text-align:center"><b>Người khai</b><br/>(Ký, ghi rõ họ tên)</td>' +
        "</tr></table>",
    ].join("");

    doc.render({ content: bodyHtml });

    const blob = doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    saveAs(blob, "Ke_khai_thanh_tich_GTNS.docx");
  });
}
